import React, { useState } from "react";
import { AddedPhotoStatus, RecipeTagManifest, UserRecipe, AppUserRecipe } from "@eatbetter/recipes-shared";
import { View } from "react-native";
import {
  THeading2,
  Spacer,
  globalStyleConstants,
  Photo,
  TBody,
  ButtonRectangle,
  displayUnexpectedErrorAndLog,
  Pressable,
  CurrentEnvironment,
  TTertiary,
} from "@eatbetter/ui-shared";
import { NvpTable } from "./NvpTable";
import { Link, useNavigate } from "react-router-dom";
import { AdminRecipesScreenIdNav } from "../screens/AdminRecipesScreen";
import {
  AuthorComponent,
  BookComponent,
  CategorizedIngredients,
  IndexComponent,
  Ingredients,
  Instructions,
  PublisherComponent,
  TagsComponent,
  TimeComponent,
  YieldComponent,
} from "./AdminRecipeComponents";
import { ParsedIngredientSummary } from "@eatbetter/items-shared";
import { AdminRecipeEditScreenNav } from "../screens/AdminRecipeEditScreen";
import { deleteUserRecipe, setUserRecipeFullAccess } from "../lib/recipes/AdminRecipesThunks";
import { ReprocessRecipeModal } from "./ReprocessRecipeModal";
import { useDispatch } from "../lib/AdminRedux";
import { AdminRecipeDocumentsScreenNav } from "../screens/AdminRecipeDocumentsScreen";
import { getPhotoKey, getUrlForAsset, PhotoRef } from "@eatbetter/photos-shared";
import { colorPalette } from "@eatbetter/ui-shared/src/components/themes/Colors";

const editConfirmMessage =
  "You are about to edit a user recipe on behalf of the user! If you save the recipe, no future " +
  "changes to the source recipe will be propagated to the user recipe.";

const deleteConfirmMessage = "Are you sure you want to delete this user recipe?";

export const UserRecipeView = React.memo(
  (props: {
    recipe: UserRecipe | AppUserRecipe;
    parsed?: ParsedIngredientSummary[];
    tagManifest?: RecipeTagManifest;
    onRecipeUpdated?: (r: AppUserRecipe) => void;
  }) => {
    const nav = useNavigate();
    const dispatch = useDispatch();
    const [waiting, setWaiting] = useState(false);
    const [showReprocessDialog, setShowReprocessDialog] = useState(false);

    const recipe = props.recipe;
    const externalSourceRecipe = !!recipe.sourceRecipeId && recipe.sourceRecipeId.startsWith("e:");

    const onEdit = () => {
      if (window.confirm(editConfirmMessage)) {
        nav(AdminRecipeEditScreenNav.getPath(recipe.id));
      }
    };

    const onDelete = async () => {
      try {
        if (window.confirm(deleteConfirmMessage)) {
          const updated = await dispatch(
            deleteUserRecipe({ userId: recipe.userId, recipeId: recipe.id, version: recipe.version }, setWaiting)
          );
          props.onRecipeUpdated?.(updated);
        }
      } catch (err) {
        displayUnexpectedErrorAndLog("Error reprocessing user recipe", err);
      }
    };

    const onToggleFullAccess = async () => {
      if (recipe.ac) {
        throw new Error("Recipe is access checked. This should only be called for access checked false");
      }

      if (recipe.faInfo?.type === "legacy") {
        throw new Error("Full access via legacy cannot be changed");
      }

      try {
        const updated = await dispatch(
          setUserRecipeFullAccess({ recipeId: recipe.id, access: recipe.faInfo?.type === "adminRecipe" ? null : {} })
        );
        props.onRecipeUpdated?.(updated);
      } catch (err) {
        displayUnexpectedErrorAndLog("Error toggling full access", err);
      }
    };

    const paywall = recipe.ac ? recipe.pw : recipe.paywall;
    const paywallJson = paywall ? JSON.stringify(paywall) : "";
    const importedFromJson = !recipe.ac && recipe.importedFrom ? JSON.stringify(recipe.importedFrom) : "-";

    return (
      <>
        <View>
          <THeading2>[User Recipe] {recipe.title}</THeading2>
          <Spacer vertical={1} />
          {recipe.ac && (
            <View style={{ backgroundColor: "yellow" }}>
              <TBody>
                You are viewing the AppUserRecipe, which is the exact data that is sent down to the user. To view the
                full server representation of the recipe,{" "}
                <Link to={AdminRecipesScreenIdNav.getPath(recipe.id)} target="_blank">
                  view it here
                </Link>
              </TBody>
            </View>
          )}
          <Spacer vertical={1} />
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <View style={{ width: globalStyleConstants.unitSize * 12 }}>
              <ButtonRectangle onPress={onEdit} type="submit" title="Edit" waiting={waiting} />
            </View>
            <Spacer horizontal={1} />
            <View>
              <ButtonRectangle onPress={() => setShowReprocessDialog(true)} type="submit" title="Reprocess" />
            </View>
            <Spacer horizontal={1} />
            <View>
              <ButtonRectangle onPress={onDelete} type="submit" title="Delete" waiting={waiting} />
            </View>
          </View>
          <Spacer vertical={1} />
          <table>
            <tbody>
              <tr>
                <td>
                  <TBody fontWeight="heavy">Meta</TBody>
                </td>
              </tr>
              <NvpTable name="ID" value={recipe.id} />
              {externalSourceRecipe && (
                <NvpTable name="Source Recipe ID">
                  <Link to={AdminRecipesScreenIdNav.getPath(recipe.sourceRecipeId)} target="_blank">
                    {recipe.sourceRecipeId}
                  </Link>
                </NvpTable>
              )}
              {!externalSourceRecipe && <NvpTable name="Source Recipe ID" value={recipe.sourceRecipeId} />}
              <NvpTable name="Status" value={recipe.status} />
              <NvpTable name="Video Status" value={recipe.videoStatus ? JSON.stringify(recipe.videoStatus) : "-"} />
              <NvpTable
                name="Added Photo Status"
                value={recipe.addedPhotoStatus ? JSON.stringify(recipe.addedPhotoStatus) : "-"}
              />
              <NvpTable name="Created" value={new Date(recipe.created).toString()} />
              <NvpTable
                name="Deleted"
                value={(recipe.deleted ?? false).toString()}
                backgroundColor={recipe.deleted ? "red" : undefined}
              />
              <NvpTable
                name="Archived"
                value={(recipe.archived ?? false).toString()}
                backgroundColor={recipe.archived ? "red" : undefined}
              />
              <NvpTable name="Access Checked" value={String(recipe.ac)} />
              {recipe.ac && <NvpTable name="Full Access" value={String(recipe.fa)} />}
              {!recipe.ac && (
                <NvpTable name="Full Access Info">
                  <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <TBody opacity="medium">{recipe.faInfo ? JSON.stringify(recipe.faInfo) : "undefined"}</TBody>
                    <Spacer horizontal={1} />
                    <Pressable onPress={onToggleFullAccess}>
                      <TBody color={colorPalette.skyBlue}>
                        {recipe.faInfo?.type === "adminRecipe" ? "revoke" : "grant"}
                      </TBody>
                    </Pressable>
                  </View>
                </NvpTable>
              )}
              <NvpTable name="User ID" value={recipe.userId} />
              <NvpTable name="Household ID" value={recipe.householdId ?? "-"} />
              <NvpTable name="Version" value={recipe.version} />
              <NvpTable name="Source" value={JSON.stringify(recipe.source)} />
              {recipe.source.type === "url" && (
                <NvpTable name="Source URL">
                  <TBody>
                    <a href={recipe.source.url} target="_blank" rel="noreferrer">
                      {recipe.source.url}
                    </a>
                  </TBody>
                </NvpTable>
              )}
              {recipe.source.type === "userPhoto" && (
                <>
                  <NvpTable name="Source Photos">
                    {recipe.source.photos.map((p, idx) => (
                      <SmallPhoto photo={p} key={idx} />
                    ))}
                  </NvpTable>
                  {!recipe.ac && <NvpTable name="Processing Status" value={JSON.stringify(recipe.processingMeta)} />}
                </>
              )}
              <NvpTable
                name="Source Recipe Version"
                value={
                  typeof recipe.sourceRecipeVersion === "object"
                    ? JSON.stringify(recipe.sourceRecipeVersion)
                    : recipe.sourceRecipeVersion ?? "-"
                }
              />
              <NvpTable name="Source Recipe Meta Version" value={recipe.sourceRecipeMetaVersion ?? "-"} />
              <NvpTable name="Paywall" value={paywallJson} />
              {/* importedFrom is server-only, so it is only present on the full server representation */}
              {!recipe.ac && <NvpTable name="Imported From" value={importedFromJson} />}
              <NvpTable name="Edit Count" value={recipe.stats.edited ?? 0} />
              <NvpTable name="Add to List Count" value={recipe.stats.addedToList ?? 0} />
              <NvpTable name="Cooked Count" value={recipe.stats.cooked ?? 0} />
              <NvpTable name="Notes" value={recipe.notes?.text ?? ""} />
              <NvpTable name="Tags" value={JSON.stringify(recipe.tags)} />
              {!recipe.ac && (
                <NvpTable name="Search Indexing">
                  <IndexComponent recipe={recipe} />
                </NvpTable>
              )}
              <NvpTable name="">
                <TBody>
                  <Link to={AdminRecipeDocumentsScreenNav.getPath(recipe.id)} target="_blank">
                    Docs
                  </Link>
                </TBody>
              </NvpTable>
              <tr>
                <td>
                  <TBody fontWeight="heavy">Recipe</TBody>
                </td>
              </tr>
              <NvpTable name="Title">
                <THeading2>{recipe.title}</THeading2>
              </NvpTable>
              <NvpTable name="Photo">
                <View style={{ width: globalStyleConstants.unitSize * 24 }}>
                  <Photo source={recipe.photo} style="fullWidthLarge" resizeMode="contain" sourceSize="w1290" />
                </View>
              </NvpTable>
              <NvpTable name="Media">
                <View style={{ flexDirection: "row" }}>
                  {recipe.media.map((p, idx) => (
                    <View key={idx}>
                      <SmallPhoto photo={p.photo} key={idx} deleted={!!p.deleted} />
                      <TTertiary>{p.source.type}</TTertiary>
                      <AddedPhotoStatusText status={recipe.addedPhotoStatus?.[getPhotoKey(p.photo)]} />
                    </View>
                  ))}
                </View>
              </NvpTable>
              {!!recipe.photo && <NvpTable name="Photo type" value={recipe.photo.type} />}
              <NvpTable name="Author">
                <AuthorComponent author={recipe.author} />
              </NvpTable>
              <NvpTable name="Book">
                <BookComponent book={recipe.book} />
              </NvpTable>
              <NvpTable name="Publisher">
                <PublisherComponent publisher={recipe.publisher} />
              </NvpTable>
              <NvpTable name="Description">
                <TBody>{recipe.description}</TBody>
              </NvpTable>
              <NvpTable name="Time">
                <TimeComponent time={recipe.time} />
              </NvpTable>
              <NvpTable name="Yield">
                <YieldComponent yield={recipe.recipeYield} />
              </NvpTable>
              {!!props.tagManifest && (
                <NvpTable name="Tags">
                  <TagsComponent tags={recipe.tags} manifest={props.tagManifest} />
                </NvpTable>
              )}
              <NvpTable name="Ingredients">
                <Ingredients ingredients={recipe.ingredients} />
              </NvpTable>
              <NvpTable name="Instructions">
                <Instructions instructions={recipe.instructions} />
              </NvpTable>
              {props.parsed && (
                <NvpTable name="Categorized">
                  <CategorizedIngredients items={props.parsed} />
                </NvpTable>
              )}
            </tbody>
          </table>
        </View>
        <ReprocessRecipeModal
          recipe={recipe}
          open={showReprocessDialog}
          onClose={() => setShowReprocessDialog(false)}
        />
      </>
    );
  }
);

const SmallPhoto = (props: { photo: PhotoRef; deleted?: boolean }) => {
  return (
    <a
      href={getUrlForAsset(CurrentEnvironment.configEnvironment(), props.photo, "original")}
      target="_blank"
      rel="noreferrer"
    >
      <img
        src={getUrlForAsset(CurrentEnvironment.configEnvironment(), props.photo, "w288")}
        style={{
          padding: 5,
          borderWidth: 2,
          borderColor: props.deleted ? "red" : "transparent",
          borderStyle: "solid",
        }}
        height={150}
      />
    </a>
  );
};

const AddedPhotoStatusText = (props: { status: AddedPhotoStatus | undefined }) => {
  if (!props.status) {
    return null;
  }

  const { status, result, ts } = props.status;
  return (
    <TTertiary>
      {result ?? status} {new Date(ts).toLocaleTimeString()}
    </TTertiary>
  );
};
